import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ListTree,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  Search,
  SlidersHorizontal,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import { Badge } from "src/components/ui/Badge";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import Pagination from "src/components/ui/Pagination";
import Skeleton from "src/components/ui/Skeleton";
import AddCategoryModal from "src/components/modals/AddCategoryModal";
import AddSubCategoryModal from "src/components/modals/AddSubCategoryModal";
import BulkImportModal, {
  BulkImportColumn,
} from "src/components/bulk-import/BulkImportModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as categoryActions from "src/redux/slices/categorySlice";
import { Category, SubCategory } from "src/types/category.types";
import { toast } from "react-toastify";
import { formatCreatedBy } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import categoryApis from "src/api/category";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const CATEGORY_IMPORT_COLUMNS: BulkImportColumn[] = [
  { key: "name", label: "Name", required: true, example: "Electronics" },
  { key: "description", label: "Description", example: "Electronic goods" },
  { key: "status", label: "Status", example: "active" },
  {
    key: "subCategoryName",
    label: "Sub Category Name",
    example: "",
    aliases: ["subcategory name", "sub category"],
  },
  {
    key: "subCategoryDescription",
    label: "Sub Category Description",
    example: "",
    aliases: ["subcategory description"],
  },
  {
    key: "subCategoryStatus",
    label: "Sub Category Status",
    example: "",
    aliases: ["subcategory status"],
  },
];

// Row 1 is a plain category. Rows 2-3 share the Name "Apparel" — that's
// how sub-categories are added: repeat the category Name across rows,
// one Sub Category Name per row.
const CATEGORY_IMPORT_SAMPLE_ROWS: Array<Record<string, string | number>> = [
  {
    name: "Electronics",
    description: "Electronic goods",
    status: "active",
  },
  {
    name: "Apparel",
    status: "active",
    subCategoryName: "Men",
  },
  {
    name: "Apparel",
    status: "active",
    subCategoryName: "Women",
  },
];

const CategoryListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editRow, setEditRow] = useState<Category | null>(null);

  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [subParent, setSubParent] = useState<Category | null>(null);
  const [editSubRow, setEditSubRow] = useState<SubCategory | null>(null);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<
    (Category | (SubCategory & { parentCategoryId?: string })) | null
  >(null);
  const [statusUpdate, setStatusUpdate] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<Category | null>(null);
  const [deleteRequested, setDeleteRequested] = useState(false);

  const [showDeleteSubModal, setShowDeleteSubModal] = useState(false);
  const [deleteSubRow, setDeleteSubRow] = useState<
    (SubCategory & { parentCategoryId?: string }) | null
  >(null);
  const [deleteSubRequested, setDeleteSubRequested] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");

  const { categoryList, editCategory, deleteCategory, deleteSubCategory } =
    useAppSelector((state) => state.category);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreate =
    isTenantAdmin || (permissions?.categories || []).includes("create");
  const canEdit =
    isTenantAdmin || (permissions?.categories || []).includes("edit");
  const canToggle =
    isTenantAdmin || (permissions?.categories || []).includes("delete");
  const dispatch = useAppDispatch();

  const rows: Category[] = categoryList?.data?.data || [];

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, limit]);

  useEffect(() => {
    if (editCategory.data && statusUpdate) {
      toast.success(
        `${statusRow && "parentCategoryId" in statusRow && statusRow.parentCategoryId ? "Subcategory" : "Category"} modified successfully`
      );
      dispatch(categoryActions.resetEditCategory());
      setStatusUpdate(false);
      handleStatusModalClose();
      getList(categoryList?.data?.pagination?.currentPage || 1);
    } else if (editCategory.error && statusUpdate) {
      toast.error(`Error: ${editCategory.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCategory, dispatch, statusUpdate]);

  useEffect(() => {
    if (deleteCategory.data && deleteRequested) {
      toast.success("Category deleted successfully");
      dispatch(categoryActions.resetDeleteCategory());
      setDeleteRequested(false);
      handleDeleteModalClose();
      getList(1);
    } else if (deleteCategory.error && deleteRequested) {
      toast.error(`Error: ${deleteCategory.error}`);
      setDeleteRequested(false);
      handleDeleteModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteCategory, dispatch, deleteRequested]);

  useEffect(() => {
    if (deleteSubCategory.data && deleteSubRequested) {
      toast.success("Subcategory deleted successfully");
      dispatch(categoryActions.resetDeleteSubCategory());
      setDeleteSubRequested(false);
      handleDeleteSubModalClose();
      getList(categoryList?.data?.pagination?.currentPage || 1);
    } else if (deleteSubCategory.error && deleteSubRequested) {
      toast.error(`Error: ${deleteSubCategory.error}`);
      setDeleteSubRequested(false);
      handleDeleteSubModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSubCategory, dispatch, deleteSubRequested]);

  const getList = (page: number) =>
    dispatch(
      categoryActions.getCategoryListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      })
    );

  const handlePageChange = (page: number) => getList(page);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("");
  };
  const hasActiveFilters = !!(search || status);

  const toggleExpand = (id?: string) => {
    if (!id) return;
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCategoryModalClose = () => {
    setShowCategoryModal(false);
    setEditRow(null);
  };
  const handleCategoryModalSuccess = () => {
    handleCategoryModalClose();
    getList(categoryList?.data?.pagination?.currentPage || 1);
  };

  const handleSubCategoryModalClose = () => {
    setShowSubCategoryModal(false);
    setSubParent(null);
    setEditSubRow(null);
  };
  const handleSubCategoryModalSuccess = () => {
    handleSubCategoryModalClose();
    getList(categoryList?.data?.pagination?.currentPage || 1);
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    if ("parentCategoryId" in statusRow && statusRow.parentCategoryId) {
      dispatch(
        categoryActions.editCategoryRequest({
          _id: statusRow.parentCategoryId,
          subCategoryUpdate: {
            _id: statusRow._id,
            status: statusRow.status === "active" ? "inactive" : "active",
          },
        })
      );
      return;
    }

    dispatch(
      categoryActions.editCategoryRequest({
        _id: statusRow._id,
        status: statusRow.status === "active" ? "inactive" : "active",
      })
    );
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteRow(null);
  };
  const onConfirmDelete = () => {
    if (!deleteRow?._id) return;
    setDeleteRequested(true);
    dispatch(categoryActions.deleteCategoryRequest(deleteRow._id));
  };

  const handleDeleteSubModalClose = () => {
    setShowDeleteSubModal(false);
    setDeleteSubRow(null);
  };
  const onConfirmDeleteSub = () => {
    if (!deleteSubRow?._id || !deleteSubRow?.parentCategoryId) return;
    setDeleteSubRequested(true);
    dispatch(
      categoryActions.deleteSubCategoryRequest({
        categoryId: deleteSubRow.parentCategoryId,
        subCategoryId: deleteSubRow._id,
      })
    );
  };

  return (
    <DashboardLayout
      meta={{
        title: "Categories | Invyntra",
        description:
          "Track and manage all product categories with detailed information.",
        keywords: "categories, products, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <ListTree size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Categories
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage product categories and sub categories.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {canCreate && (
              <Button
                variant="outline"
                rounded="full"
                onClick={() => setShowImportModal(true)}
              >
                <Upload size={16} className="mr-1.5" />
                Bulk import
              </Button>
            )}
            {canCreate && (
              <Button
                rounded="full"
                onClick={() => {
                  setEditRow(null);
                  setShowCategoryModal(true);
                }}
              >
                <Plus size={16} className="mr-1" />
                Add Category
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search categories..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Status"
                value={status}
                onChange={(val) => setStatus(val as "" | "active" | "inactive")}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Category list"
            description="Search and manage your product categories and sub categories."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <div className="border border-gray-100 rounded-xl overflow-hidden overflow-x-scroll dark:border-gray-700">
            <table className="w-full text-sm text-left text-gray-700 min-w-max dark:text-gray-200">
              <thead className="bg-gray-50 text-gray-500 text-xs border-b border-gray-100 dark:bg-gray-900/60 dark:text-gray-400 dark:border-gray-700">
                <tr>
                  <th className="py-3.5 px-4 font-medium whitespace-nowrap w-10" />
                  <th className="py-3.5 px-4 font-medium whitespace-nowrap">
                    Name
                  </th>
                  <th className="py-3.5 px-4 font-medium whitespace-nowrap">
                    Created By
                  </th>
                  <th className="py-3.5 px-4 font-medium whitespace-nowrap">
                    Status
                  </th>
                  <th className="py-3.5 px-4 font-medium whitespace-nowrap">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {categoryList.loading ? (
                  Array.from({ length: 6 }).map((_, rowIndex) => (
                    <tr
                      key={`skeleton-row-${rowIndex}`}
                      className="border-b border-gray-100 last:border-b-0 dark:border-gray-700"
                    >
                      <td className="py-3 px-4 align-middle" />
                      {Array.from({ length: 4 }).map((__, colIdx) => (
                        <td key={`skeleton-col-${colIdx}`} className="py-3 px-4 align-middle">
                          <Skeleton className="h-4 w-full max-w-[180px] rounded-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : rows.length > 0 ? (
                  rows.map((row) => {
                    const subCategories = row.subCategories || [];
                    const isExpanded = !!(row._id && expandedRows.has(row._id));
                    return (
                      <React.Fragment key={row._id}>
                        <tr className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/70 transition dark:border-gray-700 dark:hover:bg-gray-700/50">
                          <td className="py-3 px-4 align-middle">
                            <button
                              className="p-1 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition dark:hover:bg-gray-700 dark:hover:text-gray-200"
                              onClick={() => toggleExpand(row._id)}
                              title={
                                subCategories.length
                                  ? isExpanded
                                    ? "Hide subcategories"
                                    : "Show subcategories"
                                  : undefined
                              }
                            >
                              {isExpanded ? (
                                <ChevronDown size={15} />
                              ) : (
                                <ChevronRight size={15} />
                              )}
                            </button>
                          </td>
                          <td className="py-3 px-4 align-middle">
                            <div>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {row.name}
                              </span>
                              {subCategories.length > 0 && (
                                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                                  ({subCategories.length} sub{" "}
                                  {subCategories.length === 1
                                    ? "category"
                                    : "categories"}
                                  )
                                </span>
                              )}
                            </div>
                            {row.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {row.description}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 align-middle">
                            {formatCreatedBy(row.createdBy)}
                          </td>
                          <td className="py-3 px-4 align-middle">
                            <Badge
                              text={row.status === "active" ? "Active" : "Inactive"}
                              color={row.status === "active" ? "green" : "gray"}
                            />
                          </td>
                          <td className="py-3 px-4 align-middle">
                            {!canEdit && !canToggle ? null : (
                              <Popover
                                trigger={
                                  <button
                                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                                    title="Actions"
                                  >
                                    <MoreVertical size={16} />
                                  </button>
                                }
                              >
                                {(close) => (
                                  <div className="w-48 py-1.5">
                                    {canEdit && (
                                      <button
                                        className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                                        onClick={() => {
                                          close();
                                          setEditRow(row);
                                          setShowCategoryModal(true);
                                        }}
                                      >
                                        <Pencil size={14} />
                                        Edit
                                      </button>
                                    )}
                                    {canCreate && (
                                      <button
                                        className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                                        onClick={() => {
                                          close();
                                          setSubParent(row);
                                          setEditSubRow(null);
                                          setShowSubCategoryModal(true);
                                          if (row._id) {
                                            setExpandedRows((prev) =>
                                              new Set(prev).add(row._id as string)
                                            );
                                          }
                                        }}
                                      >
                                        <Plus size={14} />
                                        Add subcategory
                                      </button>
                                    )}
                                    {canToggle && (
                                      <button
                                        className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                                        onClick={() => {
                                          close();
                                          setStatusRow(row);
                                          setShowStatusModal(true);
                                        }}
                                      >
                                        <Power size={14} />
                                        {row.status === "active"
                                          ? "Deactivate"
                                          : "Activate"}
                                      </button>
                                    )}
                                    {canToggle && (
                                      <button
                                        className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                                        onClick={() => {
                                          close();
                                          setDeleteRow(row);
                                          setShowDeleteModal(true);
                                        }}
                                      >
                                        <Trash2 size={14} />
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                )}
                              </Popover>
                            )}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="border-b border-gray-100 last:border-b-0 dark:border-gray-700">
                            <td />
                            <td colSpan={4} className="py-3 px-4 bg-gray-50/60 dark:bg-gray-900/30">
                              {subCategories.length === 0 ? (
                                <div className="flex items-center justify-between py-1">
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    No subcategories yet.
                                  </span>
                                  {canCreate && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setSubParent(row);
                                        setEditSubRow(null);
                                        setShowSubCategoryModal(true);
                                      }}
                                    >
                                      <Plus size={13} className="mr-1" />
                                      Add subcategory
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="border border-gray-100 rounded-lg overflow-hidden dark:border-gray-700">
                                    <table className="w-full text-sm text-left text-gray-700 dark:text-gray-200">
                                      <thead className="bg-gray-100/70 text-gray-500 text-xs dark:bg-gray-800 dark:text-gray-400">
                                        <tr>
                                          <th className="py-2 px-3 font-medium">Name</th>
                                          <th className="py-2 px-3 font-medium">
                                            Description
                                          </th>
                                          <th className="py-2 px-3 font-medium">
                                            Created On
                                          </th>
                                          <th className="py-2 px-3 font-medium">Status</th>
                                          <th className="py-2 px-3 font-medium">Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {subCategories.map((sub) => (
                                          <tr
                                            key={sub._id}
                                            className="border-t border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-800"
                                          >
                                            <td className="py-2 px-3 font-medium text-gray-900 dark:text-gray-100">
                                              {sub.name}
                                            </td>
                                            <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                                              {sub.description || "-"}
                                            </td>
                                            <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                                              {formatDateTime(sub.createdAt, "DD/MM/YYYY")}
                                            </td>
                                            <td className="py-2 px-3">
                                              <Badge
                                                text={
                                                  sub.status === "active"
                                                    ? "Active"
                                                    : "Inactive"
                                                }
                                                color={
                                                  sub.status === "active"
                                                    ? "green"
                                                    : "gray"
                                                }
                                              />
                                            </td>
                                            <td className="py-2 px-3">
                                              <div className="flex items-center gap-1">
                                                {canEdit && (
                                                  <button
                                                    className="p-1.5 rounded-md hover:bg-primary-100 text-primary-600 dark:hover:bg-primary-900/40"
                                                    onClick={() => {
                                                      setSubParent(row);
                                                      setEditSubRow(sub);
                                                      setShowSubCategoryModal(true);
                                                    }}
                                                  >
                                                    <Pencil size={13} />
                                                  </button>
                                                )}
                                                {canToggle && (
                                                  <button
                                                    className={`p-1.5 rounded-md ${
                                                      sub.status === "active"
                                                        ? "hover:bg-red-100 text-red-600 dark:hover:bg-red-900/30"
                                                        : "hover:bg-green-100 text-green-600 dark:hover:bg-green-900/30"
                                                    }`}
                                                    onClick={() => {
                                                      setStatusRow({
                                                        ...sub,
                                                        parentCategoryId: row._id,
                                                      });
                                                      setShowStatusModal(true);
                                                    }}
                                                  >
                                                    <Power size={13} />
                                                  </button>
                                                )}
                                                {canToggle && (
                                                  <button
                                                    className="p-1.5 rounded-md hover:bg-red-100 text-red-600 dark:hover:bg-red-900/30"
                                                    onClick={() => {
                                                      setDeleteSubRow({
                                                        ...sub,
                                                        parentCategoryId: row._id,
                                                      });
                                                      setShowDeleteSubModal(true);
                                                    }}
                                                  >
                                                    <Trash2 size={13} />
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  {canCreate && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setSubParent(row);
                                        setEditSubRow(null);
                                        setShowSubCategoryModal(true);
                                      }}
                                    >
                                      <Plus size={13} className="mr-1" />
                                      Add subcategory
                                    </Button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <td
                      colSpan={5}
                      className="py-6 px-4 text-center text-gray-500 dark:text-gray-400"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {categoryList?.data?.pagination && (
            <Pagination
              currentPage={categoryList.data.pagination.currentPage}
              pageLimit={categoryList.data.pagination.pageLimit}
              totalPages={categoryList.data.pagination.totalPages}
              totalRecords={categoryList.data.pagination.totalDocs}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      </div>

      {showCategoryModal && (
        <AddCategoryModal
          open={showCategoryModal}
          category={editRow}
          onClose={handleCategoryModalClose}
          onSuccess={handleCategoryModalSuccess}
        />
      )}

      {showImportModal && (
        <BulkImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          title="Bulk import categories"
          entityLabel="categories"
          columns={CATEGORY_IMPORT_COLUMNS}
          sampleRows={CATEGORY_IMPORT_SAMPLE_ROWS}
          sampleFileName="category-import-sample.xlsx"
          onImport={(rows) => categoryApis.bulkImport(rows)}
          onImported={() => getList(1)}
          helperNote={
            <>
              To add sub-categories, repeat the same category{" "}
              <strong>Name</strong> across multiple rows and fill in Sub
              Category Name per row — see the sample sheet for an example.
              Importing a Name that already exists won't create a
              duplicate; it just adds any new sub-categories to it.
            </>
          }
        />
      )}

      {showSubCategoryModal && (
        <AddSubCategoryModal
          open={showSubCategoryModal}
          parentCategoryId={subParent?._id}
          parentCategoryName={subParent?.name}
          subCategory={editSubRow}
          onClose={handleSubCategoryModalClose}
          onSuccess={handleSubCategoryModalSuccess}
        />
      )}

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          title={
            statusRow.status === "active" ? "Deactivate" : "Activate"
          }
          message={`Are you sure you want to ${
            statusRow.status === "active" ? "deactivate" : "activate"
          } "${statusRow.name}"?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editCategory.loading}
        />
      )}

      {showDeleteModal && deleteRow?._id && (
        <ConfirmationModal
          title="Delete category"
          message={
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleteRow.name}
              </span>
              {deleteRow.subCategories?.length
                ? " and all of its subcategories"
                : ""}
              ? This action cannot be undone.
            </>
          }
          variant="danger"
          confirmLabel="Delete"
          onConfirm={onConfirmDelete}
          onCancel={handleDeleteModalClose}
          open={showDeleteModal}
          isLoading={deleteCategory.loading}
        />
      )}

      {showDeleteSubModal && deleteSubRow?._id && (
        <ConfirmationModal
          title="Delete subcategory"
          message={
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleteSubRow.name}
              </span>
              ? This action cannot be undone.
            </>
          }
          variant="danger"
          confirmLabel="Delete"
          onConfirm={onConfirmDeleteSub}
          onCancel={handleDeleteSubModalClose}
          open={showDeleteSubModal}
          isLoading={deleteSubCategory.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default CategoryListPage;
